import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import classNames from "classnames";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import en from "date-fns/locale/en";
import pl from "date-fns/locale/pl";
import { ReactComponent as MeteoLogo } from "../../../assets/tools/yourWeather/meteo.svg";
import "swiper/swiper.min.css";
import "./Weather.scss";
import {
  getDefaultPoiWhether,
  getUserPois,
  getPoiWhether,
  clearPoisWhether,
} from "../../../services/redux/weather/reducer";
import { Loader, WeatherIcon } from "../../common";
import { getTemperature, getWeatherIconName } from "./Property";
import { selectPoisFromResponse } from "../../../services/redux/weather/selectors";
import { useLinks } from "../../../services/helpers/links";
import WeatherModal from "./WeatherModal";

const langs = {
  pl,
  gb: en,
};

function Weather() {
  const {
    t,
    i18n: { language },
  } = useTranslation(["averagePrices"]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialSelected, setInitialSelected] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);
  const token = Cookies.get("user_token");
  const poisForecastData = useSelector(state => state.weather.forecast);
  const dispatch = useDispatch();
  const { login: loginPath } = useLinks();

  const Customization = () => {
    if (token) {
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        // <a
        //   href="#"
        //   className="show-more"
        //   title={t("tools:weather.adjustTitle2")}
        //   onClick={e => {
        //     e.preventDefault();
        //     setModalOpened(true);
        //   }}
        // >
        //   {t("tools:weather.adjustTitle2")}
        // </a>
        <Link
          to="/my/farm"
          className="show-more"
          title={t("tools:weather.adjustTitle2")}
        >
          {t("tools:weather.adjustTitle2")}
        </Link>
      );
    }

    const path = loginPath();
    return (
      <Link to={`${path}?redirect=my-farm`} className="show-more">
        {t("tools:weather.adjustTitle2")}
      </Link>
    );
  };

  const loadWeatherData = () => {
    if (token) {
      setIsLoading(true);
      dispatch(getUserPois())
        .then(async data => {
          const pois = selectPoisFromResponse(data);

          if (poisForecastData.length >= 0) {
            await pois.reduce(async (previousPromise, next) => {
              await previousPromise;
              return dispatch(getPoiWhether(next));
            }, Promise.resolve());
          }

          if (!poisForecastData.length && !pois.length) {
            await dispatch(getDefaultPoiWhether());
          }
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(true);
      dispatch(getDefaultPoiWhether()).finally(() => {
        setIsLoading(false);
      });
    }
  };

  const { name, forecast } = useMemo(() => {
    if (!poisForecastData.length) return { name: "", forecast: [] };

    const forecastData = poisForecastData[0];

    return {
      name: forecastData.name,
      forecast: forecastData.forecast.slice(0, 5).map(item => {
        const {
          at_date,
          weather_hourly,
          average_daytime_temperature,
          average_nighttime_temperature,
        } = item;

        return {
          name: format(at_date, "dddd", { locale: langs[language] }),
          day:
            typeof average_daytime_temperature === "number" &&
            Math.round(average_daytime_temperature),
          night:
            typeof average_nighttime_temperature === "number" &&
            Math.round(average_nighttime_temperature),
          unit: "°C",
          weather_hourly,
          date: at_date,
          propertyName: forecastData.name,
        };
      }),
    };
  }, [poisForecastData, token]);

  useEffect(() => {
    dispatch(clearPoisWhether());
    loadWeatherData();
  }, [token]);

  return (
    <>
      <div className="weather">
        {name && (
          <div
            className="details"
            style={{
              display: "flex",
              marginBottom: "4px",
              justifyContent: "space-between",
            }}
          >
            <span>{t("tools:weather.info")}</span>
            <span>
              <span style={{ marginRight: "1rem" }}>
                {t("tools:weather.for")}: {name}
              </span>
              <Customization />
            </span>
          </div>
        )}
        <div className="weather-bar">
          {forecast.length > 0 && !isLoading ? (
            <Swiper
              modules={[Navigation]}
              navigation={window.innerWidth < 1200 || forecast.length > 6}
              slidesPerView={2}
              spaceBetween={0}
              breakpoints={{
                576: {
                  slidesPerView: 2,
                  spaceBetween: 0,
                },
                768: {
                  slidesPerView: 4,
                  spaceBetween: 0,
                },
                1200: {
                  slidesPerView: 5,
                  spaceBetween: 0,
                },
              }}
            >
              {forecast.map(
                (
                  {
                    id,
                    name,
                    day,
                    night,
                    unit,
                    weather_hourly,
                    date,
                    propertyName,
                  },
                  index
                ) => {
                  return (
                    <SwiperSlide key={`${id}-${name}`}>
                      <div
                        className={classNames("weather-bar__tile", {
                          "is-today": index === 0,
                          clickable: !!token,
                        })}
                        key={`${id}-${name}`}
                        onClick={() => {
                          if (token) {
                            setInitialSelected({ date, name: propertyName });
                            setModalOpened(true);
                          }
                        }}
                      >
                        <WeatherIcon
                          forecast={
                            getWeatherIconName(weather_hourly) ||
                            "partly-cloudy-day"
                          }
                          size={64}
                          color="#00965E"
                        />
                        <div className="weather-bar__data">
                          <span className="weather-bar__name">{name}</span>
                          {typeof day === "number" ? (
                            <div className="weather-bar__temp">
                              {t("tools:weather.day")}:{" "}
                              <span>
                                {day} {unit}
                              </span>
                            </div>
                          ) : (
                            <div style={{ height: "13px" }} />
                          )}
                          {typeof night === "number" ? (
                            <div className="weather-bar__temp">
                              {t("tools:weather.night")}:{" "}
                              <span>
                                {night}
                                {unit}
                              </span>
                            </div>
                          ) : (
                            <div style={{ height: "13px" }} />
                          )}
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                }
              )}
            </Swiper>
          ) : isLoading ? (
            <Loader absolute />
          ) : (
            <div className="weather-bar__no-data">
              {t("averagePrices:noData")}
            </div>
          )}
        </div>
        <div className="weather__bottom">
          <p>
            {t("tools:weather.provider")}{" "}
            <a
              href="https://www.meteo.pl/"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              <MeteoLogo />
            </a>
          </p>
          <p className="weather__description">
            {t("tools:weather.description")}
          </p>
        </div>
      </div>
      <WeatherModal
        forecast={poisForecastData}
        isOpen={modalOpened}
        setIsOpen={val => setModalOpened(val)}
        initialSelected={initialSelected}
      />
    </>
  );
}

export default Weather;
